import React, { useCallback, useEffect, useState } from "react";
import {
    Table,
    Card,
    Typography,
    Button,
    Modal,
    Form,
    Select,
    DatePicker,
    TimePicker,
    Input,
    message,
    Row,
    Col,
    Divider,
} from "antd";
import { SearchOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import minMax from "dayjs/plugin/minMax";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import api from "../../api";
import { useNavigate } from "react-router-dom";

const { Title } = Typography;
const { Option } = Select;

dayjs.extend(minMax);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);

const FlightsPage: React.FC = () => {
    const [flights, setFlights] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState<string>("");
    const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
    const [modalOpen, setModalOpen] = useState(false);
    const [customers, setCustomers] = useState<any[]>([]);
    const [customersLoading, setCustomersLoading] = useState(false);
    const [departureFilterType, setDepartureFilterType] = useState<"in" | "out" | null>(null);
    const [departureDateRange, setDepartureDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
    const [total, setTotal] = useState(0);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });

    const [form] = Form.useForm();
    const navigate = useNavigate();

    const inDepartureDate = Form.useWatch("in_departure_date", form);
    const inArrivalDate = Form.useWatch("in_arrival_date", form);
    const outDepartureDate = Form.useWatch("out_departure_date", form);
    const outArrivalDate = Form.useWatch("out_arrival_date", form);
    const selectedCurrency = Form.useWatch("flight_cost_currency", form);

    const [manuallyEditedInArrival, setManuallyEditedInArrival] = useState(false);
    const [manuallyEditedOutArrival, setManuallyEditedOutArrival] = useState(false);

    useEffect(() => {
        if (inDepartureDate && !manuallyEditedInArrival) {
            form.setFieldsValue({ in_arrival_date: inDepartureDate });
        }
    }, [inDepartureDate, manuallyEditedInArrival, form]);

    useEffect(() => {
        if (
            inArrivalDate &&
            inDepartureDate &&
            !inArrivalDate.isSame(inDepartureDate, "day")
        ) {
            setManuallyEditedInArrival(true);
        }
    }, [inArrivalDate, inDepartureDate]);

    useEffect(() => {
        if (outDepartureDate && !manuallyEditedOutArrival) {
            form.setFieldsValue({ out_arrival_date: outDepartureDate });
        }
    }, [outDepartureDate, manuallyEditedOutArrival, form]);

    useEffect(() => {
        if (
            outArrivalDate &&
            outDepartureDate &&
            !outArrivalDate.isSame(outDepartureDate, "day")
        ) {
            setManuallyEditedOutArrival(true);
        }
    }, [outArrivalDate, outDepartureDate]);

    const fetchFlights = useCallback(async (page = 1, pageSize = 10) => {
        try {
            setLoading(true);

            const params: any = {
                page,
                page_size: pageSize,
                ordering: "-id",
            };

            if (searchText) params.search = searchText;
            if (statusFilter) params.status = statusFilter;

            if (departureDateRange?.[0] && departureDateRange?.[1]) {
                const [start, end] = departureDateRange.map((d) => d.format("YYYY-MM-DD"));

                if (departureFilterType === "in") {
                    params.in_departure_date__gte = start;
                    params.in_departure_date__lte = end;
                    params.ordering = "in_departure_date";
                } else if (departureFilterType === "out") {
                    params.out_departure_date__gte = start;
                    params.out_departure_date__lte = end;
                    params.ordering = "out_departure_date";
                }
            } else {
                if (departureFilterType === "in") {
                    params.ordering = "in_departure_date";
                } else if (departureFilterType === "out") {
                    params.ordering = "out_departure_date";
                }
            }

            const res = await api.get("/daia/flights/", { params });
            const rawData = res.data.results || res.data;

            setFlights(rawData);
            setTotal(res.data.count ?? rawData.length);
        } catch (err) {
            console.error(err);
            message.error("Failed to load DAIA flights");
        } finally {
            setLoading(false);
        }
    }, [searchText, statusFilter, departureDateRange, departureFilterType]);

    useEffect(() => {
        fetchFlights(pagination.current, pagination.pageSize);
    }, [
        fetchFlights,
        pagination.current,
        pagination.pageSize,
    ]);

    const fetchCustomers = useCallback(async (query?: string) => {
        try {
            setCustomersLoading(true);

            const res = await api.get("/daia/customers/", {
                params: {
                    page: 1,
                    page_size: 20,
                    ...(query ? { search: query } : {}),
                    ordering: "-id",
                },
            });

            setCustomers(res.data.results || res.data);
        } catch (err) {
            console.error(err);
            message.error("Failed to load DAIA customers");
        } finally {
            setCustomersLoading(false);
        }
    }, []);

    useEffect(() => {
        if (modalOpen) fetchCustomers();
    }, [modalOpen, fetchCustomers]);

    function debounce<T extends (...args: any[]) => void>(fn: T, wait = 250) {
        let t: any;

        return (...args: Parameters<T>) => {
            clearTimeout(t);
            t = setTimeout(() => fn(...args), wait);
        };
    }

    const debouncedCustomerSearch = useCallback(
        debounce((q: string) => fetchCustomers(q), 250),
        [fetchCustomers]
    );

    const handleCreate = async (values: any) => {
        const {
            in_departure_date,
            in_arrival_date,
            out_departure_date,
            out_arrival_date,
            in_departure_time,
            in_arrival_time,
            out_departure_time,
            out_arrival_time,
        } = values;

        try {
            if (
                in_departure_date &&
                in_arrival_date &&
                in_departure_date.isAfter(in_arrival_date)
            ) {
                return message.error("IN Departure date must be before or equal to IN Arrival date");
            }

            if (
                in_arrival_date &&
                out_departure_date &&
                !out_departure_date.isAfter(in_arrival_date)
            ) {
                return message.error("OUT Departure date must be after IN Arrival date");
            }

            if (
                out_departure_date &&
                out_arrival_date &&
                out_departure_date.isAfter(out_arrival_date)
            ) {
                return message.error("OUT Departure date must be before or equal to OUT Arrival date");
            }

            if (
                in_departure_time &&
                in_arrival_time &&
                !in_departure_time.isBefore(in_arrival_time)
            ) {
                return message.error("IN Departure time must be before IN Arrival time");
            }

            if (
                out_departure_time &&
                out_arrival_time &&
                !out_departure_time.isBefore(out_arrival_time)
            ) {
                return message.error("OUT Departure time must be before OUT Arrival time");
            }

            const payload = {
                ...values,
                customer_id: values.customer,
                in_departure_date: in_departure_date?.format("YYYY-MM-DD") || null,
                in_arrival_date: in_arrival_date?.format("YYYY-MM-DD") || null,
                out_departure_date: out_departure_date?.format("YYYY-MM-DD") || null,
                out_arrival_date: out_arrival_date?.format("YYYY-MM-DD") || null,
                in_departure_time: in_departure_time?.format("HH:mm:ss") || null,
                in_arrival_time: in_arrival_time?.format("HH:mm:ss") || null,
                out_departure_time: out_departure_time?.format("HH:mm:ss") || null,
                out_arrival_time: out_arrival_time?.format("HH:mm:ss") || null,
                flight_cost: values.flight_cost
                    ? parseInt(values.flight_cost.toString().replace(/,/g, ""))
                    : null,
            };

            await api.post("/daia/flights/", payload);

            message.success("DAIA flight created");
            setModalOpen(false);
            form.resetFields();
            setManuallyEditedInArrival(false);
            setManuallyEditedOutArrival(false);
            fetchFlights(pagination.current, pagination.pageSize);
        } catch (err) {
            console.error(err);
            message.error("Error creating DAIA flight");
        }
    };

    const columns = [
        {
            title: "Name",
            dataIndex: ["customer", "first_name"],
            render: (_: any, rec: any) =>
                rec.customer
                    ? `${rec.customer.first_name || ""} ${rec.customer.last_name || ""}`.trim()
                    : "-",
        },
        {
            title: "MMID",
            dataIndex: ["customer", "mmid"],
            render: (_: any, rec: any) => rec.customer?.mmid || "-",
        },
        { title: "Status", dataIndex: "status" },
        { title: "Cabin", dataIndex: "cabin" },
        { title: "IN Departure Date", dataIndex: "in_departure_date" },
        { title: "IN Departure Airport", dataIndex: "in_departure_airport" },
        { title: "IN Flight Number", dataIndex: "in_flight_number" },
        {
            title: "IN Departure Time",
            dataIndex: "in_departure_time",
            render: (time: string) =>
                time ? dayjs(time, ["HH:mm:ss", "HH:mm"]).format("HH:mm") : "-",
        },
        { title: "IN Arrival Airport", dataIndex: "in_arrival_airport" },
        {
            title: "IN Arrival Time",
            dataIndex: "in_arrival_time",
            render: (time: string) =>
                time ? dayjs(time, ["HH:mm:ss", "HH:mm"]).format("HH:mm") : "-",
        },
    ];

    return (
        <Card
            className="rounded-2xl shadow-sm"
            title={
                <Row justify="space-between" align="middle">
                    <Col>
                        <Title level={3} style={{ margin: 0 }}>
                            DAIA Flights
                        </Title>
                    </Col>
                    <Col>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setModalOpen(true)}
                            className="rounded-md"
                        >
                            New Flight
                        </Button>
                    </Col>
                </Row>
            }
        >
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={16} md={12} lg={8} style={{ maxWidth: 350 }}>
                    <Input
                        placeholder="Search by Customer Name or MMID"
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={(e) => {
                            setSearchText(e.target.value);
                            setPagination((prev) => ({ ...prev, current: 1 }));
                        }}
                        allowClear
                        className="rounded-md"
                    />
                </Col>

                <Col xs={24} sm={24} md={12} lg={8}>
                    <Input.Group compact style={{ display: "flex", gap: 8 }}>
                        <Select
                            allowClear
                            placeholder="By Status"
                            value={statusFilter}
                            onChange={(val) => {
                                setStatusFilter(val);
                                setPagination((prev) => ({ ...prev, current: 1 }));
                            }}
                            style={{ flex: 1 }}
                        >
                            <Option value="Not Started">Not Started</Option>
                            <Option value="Flight Options Sent">Flight Options Sent</Option>
                            <Option value="Waiting for Ticketing">Waiting for Ticketing</Option>
                            <Option value="E-Ticket Sent">E-Ticket Sent</Option>
                        </Select>

                        <Select
                            allowClear
                            placeholder="By Departure"
                            value={departureFilterType}
                            onChange={(val) => {
                                setDepartureFilterType(val);
                                setDepartureDateRange(null);
                                setPagination((prev) => ({ ...prev, current: 1 }));
                            }}
                            style={{ flex: 1 }}
                        >
                            <Option value="in">In-Departure</Option>
                            <Option value="out">Out-Departure</Option>
                        </Select>
                    </Input.Group>
                </Col>

                <Col xs={24} sm={24} md={12} lg={8}>
                    <DatePicker.RangePicker
                        style={{ width: "100%" }}
                        value={departureDateRange}
                        onChange={(dates) => {
                            if (!dates || dates.length !== 2) {
                                setDepartureDateRange(null);
                            } else {
                                setDepartureDateRange([dates[0]!, dates[1]!]);
                            }

                            setPagination((prev) => ({ ...prev, current: 1 }));
                        }}
                        format="YYYY-MM-DD"
                        allowClear
                        placeholder={
                            departureFilterType === "in"
                                ? ["In-Departure Start", "In-Departure End"]
                                : departureFilterType === "out"
                                    ? ["Out-Departure Start", "Out-Departure End"]
                                    : ["Select Type First", "Select Type First"]
                        }
                        disabled={!departureFilterType}
                    />
                </Col>
            </Row>

            <Table
                rowKey="id"
                loading={loading}
                dataSource={flights}
                columns={columns}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total,
                    showSizeChanger: true,
                    onChange: (page, pageSize) => {
                        setPagination({ current: page, pageSize });
                    },
                }}
                scroll={{ x: true }}
                onRow={(record) => {
                    const isPast = (() => {
                        const today = dayjs().startOf("day");
                        const inArrival = record.in_arrival_date ? dayjs(record.in_arrival_date) : null;
                        const outDepart = record.out_departure_date ? dayjs(record.out_departure_date) : null;

                        if (inArrival && outDepart) return outDepart.isBefore(today, "day");
                        if (inArrival) return inArrival.isBefore(today, "day");
                        if (outDepart) return outDepart.isBefore(today, "day");

                        return false;
                    })();

                    return {
                        onClick: () => navigate(`/daia/flights/${record.id}`),
                        style: {
                            cursor: "pointer",
                            transition: "background-color 0.2s ease",
                            backgroundColor: isPast ? "#f5f5f5" : "#ffffff",
                        },
                    };
                }}
                className="rounded-xl overflow-hidden"
            />

            <Modal
                title={<span className="text-base font-semibold">New DAIA Flight</span>}
                open={modalOpen}
                onCancel={() => {
                    setModalOpen(false);
                    form.resetFields();
                    setManuallyEditedInArrival(false);
                    setManuallyEditedOutArrival(false);
                }}
                onOk={() => {
                    form.validateFields()
                        .then(() => form.submit())
                        .catch((err) => console.error("Validation failed:", err));
                }}
                width={800}
                centered
                okButtonProps={{ className: "rounded-md" }}
                cancelButtonProps={{ className: "rounded-md" }}
                bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
            >
                <Form layout="vertical" form={form} onFinish={handleCreate}>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="customer"
                                label="Customer Name"
                                rules={[{ required: true, message: "Customer is required" }]}
                            >
                                <Select
                                    showSearch
                                    placeholder="Type name or MMID"
                                    filterOption={false}
                                    onSearch={(val) => debouncedCustomerSearch(val)}
                                    notFoundContent={customersLoading ? "Loading..." : null}
                                    loading={customersLoading}
                                    onDropdownVisibleChange={(open) => {
                                        if (open && customers.length === 0) fetchCustomers();
                                    }}
                                >
                                    {customers.map((cust: any) => (
                                        <Option key={cust.id} value={cust.id}>
                                            {cust.first_name} {cust.last_name} ({cust.mmid})
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="status"
                                label="Status"
                                rules={[{ required: true, message: "Status is required" }]}
                            >
                                <Select>
                                    <Option value="Not Started">Not Started</Option>
                                    <Option value="Flight Options Sent">Flight Options Sent</Option>
                                    <Option value="Waiting for Ticketing">Waiting for Ticketing</Option>
                                    <Option value="E-Ticket Sent">E-Ticket Sent</Option>
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item name="reservation_number" label="Reservation Number">
                                <Input placeholder="Enter reservation number" />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item name="cabin" label="Cabin">
                                <Select allowClear>
                                    <Option value="Economy">Economy</Option>
                                    <Option value="Prestige">Prestige</Option>
                                    <Option value="Business">Business</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider orientation="left">IN (→ Korea)</Divider>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="in_departure_date" label="Departure Date">
                                <DatePicker style={{ width: "100%" }} />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item name="in_arrival_date" label="Arrival Date">
                                <DatePicker
                                    style={{ width: "100%" }}
                                    disabledDate={(current) =>
                                        inDepartureDate
                                            ? current && current.isBefore(inDepartureDate, "day")
                                            : false
                                    }
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="in_flight_number" label="Flight Number">
                                <Input />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item name="in_departure_airport" label="Departure Airport">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="in_departure_time" label="Departure Time">
                                <TimePicker format="HH:mm" style={{ width: "100%" }} allowClear />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item name="in_arrival_airport" label="Arrival Airport">
                                <Select allowClear>
                                    <Option value="ICN-T1">ICN-T1</Option>
                                    <Option value="ICN-T2">ICN-T2</Option>
                                    <Option value="GMP">GMP</Option>
                                    <Option value="GMP-Private">GMP-Private</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="in_arrival_time" label="Arrival Time">
                        <TimePicker format="HH:mm" style={{ width: "100%" }} allowClear />
                    </Form.Item>

                    <Divider orientation="left">OUT (Korea →)</Divider>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="out_departure_date" label="Out Departure Date">
                                <DatePicker
                                    style={{ width: "100%" }}
                                    disabledDate={(current) =>
                                        inDepartureDate
                                            ? current && current.isBefore(inDepartureDate, "day")
                                            : false
                                    }
                                />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item name="out_arrival_date" label="Arrival Date">
                                <DatePicker
                                    style={{ width: "100%" }}
                                    disabledDate={(current) =>
                                        outDepartureDate
                                            ? current && current.isBefore(outDepartureDate, "day")
                                            : false
                                    }
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="out_flight_number" label="Flight Number">
                                <Input />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item name="out_departure_airport" label="Departure Airport">
                                <Select allowClear>
                                    <Option value="ICN-T1">ICN-T1</Option>
                                    <Option value="ICN-T2">ICN-T2</Option>
                                    <Option value="GMP">GMP</Option>
                                    <Option value="GMP-Private">GMP-Private</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="out_departure_time" label="Departure Time">
                                <TimePicker format="HH:mm" style={{ width: "100%" }} allowClear />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item name="out_arrival_airport" label="Arrival Airport">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="out_arrival_time" label="Arrival Time">
                        <TimePicker format="HH:mm" style={{ width: "100%" }} allowClear />
                    </Form.Item>

                    <Divider orientation="left">Cost</Divider>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="flight_cost_currency" label="Flight Cost Currency">
                                <Select placeholder="Select currency" allowClear>
                                    <Option value="Yen">Yen</Option>
                                    <Option value="KRW">KRW</Option>
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                name="flight_cost"
                                label="Flight Cost"
                                getValueFromEvent={(e) => parseInt(e.target.value.replace(/,/g, ""))}
                            >
                                <Input
                                    onChange={(e) => {
                                        const rawValue = e.target.value.replace(/,/g, "");
                                        const numeric = rawValue.replace(/\D/g, "");

                                        form.setFieldsValue({
                                            flight_cost: numeric ? Number(numeric).toLocaleString() : "",
                                        });
                                    }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    {selectedCurrency === "Yen" && (
                        <Form.Item
                            name="flight_cost_exchange_rate"
                            label="Exchange Rate (Yen ➝ KRW)"
                            rules={[{ required: true, message: "Exchange rate required for Yen" }]}
                        >
                            <Input type="number" step="0.01" />
                        </Form.Item>
                    )}

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name="memo" label="Memo">
                                <Input.TextArea placeholder="Enter memo" rows={3} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </Card>
    );
};

export default FlightsPage;